"use client";

import { useState, useCallback } from "react";
import type { AuthUser } from "./types";

export interface LocalAuthState {
  ready: boolean;
  authenticated: boolean;
  user: AuthUser | null;
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  identityToken: string | null;
  selectAddress: (address: string) => void;
  showModal: boolean;
  closeModal: () => void;
}

const LOCAL_AUTH_ADDRESS_KEY = "cede-local-auth-address";

/**
 * Local authentication provider for development.
 * Bypasses Privy/TON and allows manual wallet selection.
 * Persists the selected address in localStorage so it survives hard refresh.
 */
export const useLocalAuth = (): LocalAuthState => {
  const [selectedAddress, setSelectedAddress] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(LOCAL_AUTH_ADDRESS_KEY);
  });
  const [showModal, setShowModal] = useState(false);

  const selectAddress = useCallback((address: string) => {
    const normalized = address.toLowerCase();
    setSelectedAddress(normalized);
    localStorage.setItem(LOCAL_AUTH_ADDRESS_KEY, normalized);
    setShowModal(false);
  }, []);

  const login = useCallback(() => {
    setShowModal(true);
  }, []);

  const logout = useCallback(async () => {
    setSelectedAddress(null);
    localStorage.removeItem(LOCAL_AUTH_ADDRESS_KEY);
  }, []);

  const getAccessToken = useCallback(async () => {
    return selectedAddress;
  }, [selectedAddress]);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const user: AuthUser | null = selectedAddress
    ? {
        id: selectedAddress,
        wallet: { address: selectedAddress },
        defiAddress: selectedAddress,
      }
    : null;

  return {
    ready: true,
    authenticated: !!selectedAddress,
    user,
    login,
    logout,
    getAccessToken,
    identityToken: selectedAddress,
    selectAddress,
    showModal,
    closeModal,
  };
};
